import { parseMessage } from './smileys.ts';

/**
 * The reader's half of «Поиск по сообщениям»: deciding when a query is one, and
 * finding in a message the same words the server found.
 *
 * It has to agree with server/src/search.ts, and the agreement is what makes the
 * highlight honest — a match drawn where the index did not find one is the app
 * telling the reader something untrue about why the message came back.
 */

/** Below this a query is not sent at all, which is search.ts's rule on the server. */
export const SEARCH_MIN_TERM = 2;

const WORD = /[\p{L}\p{N}]/u;
const NOT_WORD = /[^\p{L}\p{N}]+/u;

/**
 * Case and ё, exactly as the index folds them. One character for one, so an
 * offset into the result is an offset into what was passed in.
 */
export function normalizeSearch(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е');
}

/** The words of a query that are long enough to search for. */
export function queryTerms(query: string): string[] {
  return normalizeSearch(query)
    .split(NOT_WORD)
    .filter((term) => term.length >= SEARCH_MIN_TERM);
}

/** Whether this is worth asking the server about yet. */
export function searchable(query: string): boolean {
  return queryTerms(query).length > 0;
}

export interface SearchRun {
  value: string;
  hit: boolean;
}

/**
 * Where each of the query's words begins inside a message.
 *
 * By the beginning of a word rather than anywhere inside one, because that is
 * what FTS5 answered with: «догов» matches «договор», and «говор» matches
 * nothing. Drawing a highlight on a substring the index would not have found
 * would make the two disagree in the one place the reader can see both.
 */
export function findMatches(text: string, query: string): { index: number; length: number }[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return [];

  const haystack = normalizeSearch(text);
  // Folding is one character for one for everything this app will ever hold; if
  // some alphabet proves otherwise, no highlight is better than a sliding one.
  if (haystack.length !== text.length) return [];

  const found: { index: number; length: number }[] = [];
  for (const term of terms) {
    for (let from = 0; ; ) {
      const at = haystack.indexOf(term, from);
      if (at === -1) break;
      if (at === 0 || !WORD.test(haystack[at - 1]!)) found.push({ index: at, length: term.length });
      from = at + 1;
    }
  }

  // Two words of one query can land on the same letters — «дог договор» — and
  // two overlapping marks would nest rather than sit side by side.
  found.sort((left, right) => left.index - right.index);
  const merged: { index: number; length: number }[] = [];
  for (const match of found) {
    const last = merged[merged.length - 1];
    if (last && match.index <= last.index + last.length) {
      last.length = Math.max(last.length, match.index + match.length - last.index);
      continue;
    }
    merged.push({ ...match });
  }
  return merged;
}

/** A piece of text cut into what matched and what did not, in order. */
export function splitHighlight(text: string, query: string): SearchRun[] {
  const matches = findMatches(text, query);
  if (matches.length === 0) return [{ value: text, hit: false }];

  const runs: SearchRun[] = [];
  let at = 0;
  for (const match of matches) {
    if (match.index > at) runs.push({ value: text.slice(at, match.index), hit: false });
    runs.push({ value: text.slice(match.index, match.index + match.length), hit: true });
    at = match.index + match.length;
  }
  if (at < text.length) runs.push({ value: text.slice(at), hit: false });
  return runs;
}

/**
 * A message as one line of plain text: line breaks collapsed, smiley codes
 * gone. The codec rather than a second regexp for the codes, so a row in the
 * results never shows `:wink:` — and so a query cannot mark the letters of a
 * code that the reader was never shown as letters.
 */
export function plainBody(body: string): string {
  return parseMessage(body)
    .map((part) => (part.type === 'text' ? part.value : ' '))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** How much of a message to keep in front of the word that was found. */
const LEAD = 24;

/**
 * The one line under a name in the results: the message, or the part of it the
 * match is in. A hit three hundred characters into a long message would
 * otherwise be a row of text with nothing in it that was searched for.
 */
export function snippetAround(body: string, query: string, budget = 140): string {
  const flat = plainBody(body);
  const [first] = findMatches(flat, query);

  const start = !first || first.index <= LEAD ? 0 : cutBefore(flat, first.index - LEAD);
  const head = start > 0 ? '…' : '';
  if (flat.length - start <= budget) return head + flat.slice(start);

  // Cut between words, unless the word runs longer than the whole budget — then
  // there is nothing to cut between and the row takes it mid-way.
  const boundary = cutBefore(flat, start + budget);
  const end = boundary > start ? boundary : start + budget;
  return `${head}${flat.slice(start, end).trimEnd()}…`;
}

/** Back up to the start of the word `at` falls inside, so a cut never halves one. */
function cutBefore(text: string, at: number): number {
  let index = Math.min(Math.max(at, 0), text.length);
  while (index > 0 && WORD.test(text[index - 1]!)) index -= 1;
  return index;
}
